import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Send, CheckCircle } from 'lucide-react';
import { authService } from '../../services/authService';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Veuillez saisir votre adresse email');
      return;
    }
    
    if (!isValidEmail(email)) {
      setError('Veuillez saisir une adresse email valide');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      setMessage('');
      
      const response = await authService.forgotPassword(email.trim());
      
      if (response.success) {
        setEmailSent(true);
        setMessage('Un email de récupération a été envoyé à votre adresse email.');
      } else {
        setError(response.message || 'Erreur lors de l\'envoi de l\'email');
      }
    } catch (error) {
      setError('Erreur lors de l\'envoi de l\'email. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleResendEmail = async () => {
    await handleSubmit({ preventDefault: () => {} });
  };

  if (emailSent) {
    return (
      <div className="forgot-password-page">
        <Container fluid className="min-vh-100 d-flex align-items-center justify-content-center">
          <Row className="w-100 justify-content-center">
            <Col xs={12} sm={10} md={6} lg={4}>
              <Card className="forgot-card shadow-lg border-0">
                <Card.Body className="p-4 p-md-5 text-center">
                  <div className="success-icon mb-4">
                    <CheckCircle size={64} className="text-success" />
                  </div>
                  
                  <h3 className="fw-bold text-dark mb-3">
                    Email envoyé !
                  </h3>
                  
                  <p className="text-muted mb-4">
                    Nous avons envoyé un lien de récupération à <strong>{email}</strong>
                  </p>
                  
                  <Alert variant="info" className="mb-4">
                    <div className="small">
                      <strong>Instructions :</strong>
                      <ol className="mt-2 mb-0 text-start">
                        <li>Vérifiez votre boîte de réception</li>
                        <li>Cliquez sur le lien dans l'email</li>
                        <li>Créez votre nouveau mot de passe</li>
                      </ol>
                    </div>
                  </Alert>
                  
                  <div className="d-grid gap-2">
                    <Button
                      variant="outline-primary"
                      onClick={handleResendEmail}
                      disabled={loading}
                    >
                      {loading ? 'Envoi...' : 'Renvoyer l\'email'}
                    </Button>
                    
                    <Link to="/login" className="btn btn-primary">
                      <ArrowLeft size={16} className="me-2" />
                      Retour à la connexion
                    </Link>
                  </div>
                  
                  <div className="mt-4 pt-3 border-top">
                    <small className="text-muted">
                      L'email peut prendre quelques minutes à arriver.<br />
                      Pensez à vérifier vos spams.
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="forgot-password-page">
      <Container fluid className="min-vh-100 d-flex align-items-center justify-content-center">
        <Row className="w-100 justify-content-center">
          <Col xs={12} sm={10} md={6} lg={4}>
            <Card className="forgot-card shadow-lg border-0">
              <Card.Body className="p-4 p-md-5">
                {/* Header */}
                <div className="forgot-header text-center mb-4">
                  <div className="forgot-icon mb-3">
                    <Mail size={48} className="text-primary" />
                  </div>
                  <h2 className="fw-bold text-dark mb-2">
                    Mot de passe oublié ?
                  </h2>
                  <p className="text-muted">
                    Saisissez votre email pour recevoir un lien de récupération
                  </p>
                </div>

                {/* Alert de succès */}
                {message && (
                  <Alert variant="success" className="mb-3">
                    <CheckCircle size={16} className="me-2" />
                    {message}
                  </Alert>
                )}

                {/* Alert d'erreur */}
                {error && (
                  <Alert variant="danger" className="mb-3" dismissible onClose={() => setError('')}>
                    <span className="me-2">⚠️</span>
                    {error}
                  </Alert>
                )}

                {/* Formulaire */}
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold text-dark">
                      <Mail size={16} className="me-2" />
                      Adresse email
                    </Form.Label>
                    <Form.Control
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (error) setError('');
                      }}
                      placeholder="votre@email.com"
                      className="form-control-lg"
                      autoComplete="email"
                      autoFocus
                      required
                    />
                    <Form.Text className="text-muted">
                      Nous vous enverrons un lien pour réinitialiser votre mot de passe
                    </Form.Text>
                  </Form.Group>

                  <div className="d-grid gap-2 mb-3">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="fw-semibold"
                      disabled={loading || !email.trim()}
                    >
                      {loading ? (
                        <>
                          <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <Send size={16} className="me-2" />
                          Envoyer le lien
                        </>
                      )}
                    </Button>
                  </div>
                </Form>

                {/* Lien retour */}
                <div className="text-center">
                  <Link 
                    to="/login" 
                    className="text-decoration-none text-muted d-flex align-items-center justify-content-center"
                  >
                    <ArrowLeft size={16} className="me-2" />
                    Retour à la connexion
                  </Link>
                </div>

                {/* Footer */}
                <div className="forgot-footer text-center mt-4 pt-3 border-top">
                  <small className="text-muted">
                    Besoin d'aide ? Contactez votre administrateur
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <style jsx>{`
        .forgot-password-page {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          min-height: 100vh;
        }

        .forgot-card {
          border-radius: 20px;
          backdrop-filter: blur(10px);
          background: rgba(255, 255, 255, 0.95);
          animation: slideUp 0.6s ease-out;
        }

        .forgot-icon {
          animation: float 3s ease-in-out infinite;
        }

        .success-icon {
          animation: scale 0.8s ease-out;
        }

        .form-control-lg {
          border-radius: 12px;
          border: 2px solid #e2e8f0;
          padding: 12px 16px;
          transition: all 0.3s ease;
        }

        .form-control-lg:focus {
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25);
          transform: translateY(-1px);
        }

        .btn {
          border-radius: 12px;
          padding: 12px 20px;
          transition: all 0.3s ease;
        }

        .btn-primary {
          background: linear-gradient(135deg, var(--bs-primary) 0%, var(--bs-secondary, #1d4ed8) 100%);
          border: none;
          box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
        }

        .btn-outline-primary {
          border: 2px solid var(--bs-primary);
        }

        .btn-outline-primary:hover {
          transform: translateY(-1px);
        }

        /* Animations */
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes scale {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }

        /* Liste numérotée personnalisée */
        ol {
          padding-left: 1rem;
        }

        ol li {
          margin-bottom: 0.25rem;
        }

        /* Responsive */
        @media (max-width: 576px) {
          .forgot-card {
            margin: 20px;
            border-radius: 15px;
          }
        }

        /* États disabled */
        .btn:disabled {
          opacity: 0.7;
          transform: none !important;
        }

        /* Amélioration de l'accessibilité */
        .btn:focus {
          box-shadow: 0 0 0 0.2rem rgba(255, 255, 255, 0.5);
        }
      `}</style>
    </div>
  );
};

export default ForgotPassword;